import Sentry from './sentry';
import { appInDevMode } from '../utils/helpers';

/**
 * EN-961: this used to be `scope.setExtra('instance', instance)`, which handed Sentry a live Vue
 * component instance on every handled error. Its `$props` routinely hold the resume or cover
 * letter document - `pdf-builder.data` is passed as props throughout the builder templates - and
 * reading them evaluates computed getters while we are already handling an error.
 *
 * Prop KEYS, never prop values: enough to tell which variant of the component blew up, without
 * shipping a byte of user data.
 */
export const describeComponent = (instance) => {
	try {
		if (!instance) {
			return null;
		}

		// `__name` is emitted by the <script setup> compiler; `__file` only exists in dev builds.
		const type = instance.$?.type ?? instance.$options ?? {};

		return {
			name: type.name ?? type.__name ?? 'anonymous',
			file: type.__file ?? null,
			propKeys: Object.keys(instance.$props ?? {}).slice(0, 30),
		};
	} catch {
		return { name: 'unavailable' };
	}
};

export const appGlobalErrorsHandler = ( useNotifications ) => {
	return (error, instance, info) => {
		if (appInDevMode()) {
			const notificationsStore = useNotifications();
            
			notificationsStore.notify(`Error: ${error.message} - Info: ${info}`, 'error');
			// Inertia pages outside AppLayout render no NotificationsDispatcher, so the toast alone
			// would leave the error invisible there.
			console.error(error);
		} else {
			Sentry.withScope((scope) => {
				const component = describeComponent(instance);

				// add aditional info
				scope.setTag('handler', 'vue.config.errorHandler');
				scope.setTag('component', component?.name ?? 'unknown');
				scope.setExtra('info', info);
				scope.setExtra('component', component);

				// catch error with sentry
				Sentry.captureException(error);
			});
		}
	};
};

export const appGlobalWarningsHandler = ( useNotifications ) => {
	return (message, instance, info) => {
		if (appInDevMode()) {
			const notificationsStore = useNotifications();
            
			notificationsStore.notify(`Warning: ${message} - Info: ${info}`, 'warning');
			console.warn(message);
		}
	};
};
